import { GenericForm } from '@neo/blocks/forms/generic-form'
import { Alert } from '@neo/primitives/alert'
import type { ApiResponse } from '@shared/types'
import type { UseMutateFunction } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useCallback, useMemo, useState } from 'react'
import { Link } from 'router-v6'
import { PRIVATE_FIELD_CLASS } from 'v2/configs/constants'
import { queryClient } from 'v2/configs/rq-setup'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { billingQueryKeys } from 'v2/shared/utils/query-key-management'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import { errorToast, successToast } from 'v2/utils/toasts'
import z from 'zod'
import {
  Currency,
  cardCvcMultiRuleSchema,
  cardExpiryMultiRuleSchema,
  cardHolderNameMultiRuleSchema,
  cardNumberMultiRuleSchema,
  getPostalCodeLabel,
  getZipCodeEndIcon,
  PaymentMethod,
  zipCodeValidationSchema,
} from '../billing.config'
import { openPspManually, startPspRedirect } from '../psp-redirect'
import {
  useAccountDetailsQuery,
  useBillingAddressQuery,
  useBillingCardsQuery,
  useZipCodeValidationQuery,
} from '../queries'
import type { AddCardForm, RAddCard } from '../types'
import {
  countryOptions,
  getAccountId,
  getStateOptions,
  getZipCodeStatus,
  trackPaymentFailed,
} from '../utils'

export const AddCard = ({
  onClose,
  addCard,
  isPending,
}: {
  onClose: () => void
  addCard: UseMutateFunction<RAddCard, Error, AddCardForm, unknown>
  isPending: boolean
}) => {
  const { data: accountDetails } = useAccountDetailsQuery()
  const { data: billingInformation } = useBillingAddressQuery()
  const { data: cards } = useBillingCardsQuery()
  const isIndiaCurrency = accountDetails?.account.currency === Currency.INR
  const [zipCode, setZipCode] = useState(billingInformation?.zipcode || '')
  const [selectedCountry, setSelectedCountry] = useState(
    billingInformation?.country_id || (isIndiaCurrency ? 'IN' : 'US'),
  )
  const [selectedState, setSelectedState] = useState(billingInformation?.state || '')
  // 3DS / card-verification redirect URL — set when the PSP requires it; drives
  // the manual-verify fallback shown if the auto-redirect is blocked.
  const [verifyUrl, setVerifyUrl] = useState<string | null>(null)
  const { data: zipCodeValidation, isLoading: isZipCodeValidating } = useZipCodeValidationQuery(
    zipCode,
    selectedCountry,
    selectedState,
  )
  const isFirstCard = cards?.length === 0 && !billingInformation?.zipcode
  const accountId = getAccountId()
  const defaultValues = useMemo<AddCardForm>(
    () => ({
      addressline1: billingInformation?.address_line_1 || '',
      cvc: '',
      country: billingInformation?.country_id || (isIndiaCurrency ? 'IN' : 'US'),
      number: '',
      name: '',
      year: '',
      zip: billingInformation?.zipcode || '',
      month: '',
      setuprequired: true,
      isdefault: !((cards?.length ?? 0) > 0),
      card_address: true,
      city: billingInformation?.city || '',
      state: billingInformation?.state || '',
    }),
    [billingInformation, isIndiaCurrency],
  )

  const handleZipCodeChange = useCallback((state: AddCardForm) => {
    const value = state.zip || ''
    setZipCode(value)
  }, [])

  const handleCountryChange = useCallback((state: AddCardForm) => {
    const value = state.country || ''
    setSelectedCountry(value)
  }, [])

  const handleStateChange = useCallback((state: AddCardForm) => {
    const value = state.state || ''
    setSelectedState(value)
  }, [])

  return (
    <div className="flex flex-col gap-4">
      {isIndiaCurrency && (
        <Alert variant="info" title="We currently accept Visa and Mastercard only." />
      )}
      {verifyUrl && (
        <Alert
          variant="info"
          content={
            <>
              Redirecting you to verify your card with your bank. If you're not redirected
              automatically,{' '}
              <button
                type="button"
                className="underline text-p-600"
                onClick={() => openPspManually(verifyUrl)}
                data-testid="card-3ds-manual-verify-btn"
              >
                click here to verify
              </button>
              .
            </>
          }
        />
      )}
      {/* PRIVATE_FIELD_CLASS is a blockClass: a marked node is dropped from the
          recording entirely, not masked in place. So it goes on the <input> via
          inputClassName, not the field wrapper — the label and validation error
          are siblings, and blanking those loses why an add-card failed for no
          extra safety. Address fields stay visible; they are not PCI data. */}
      <GenericForm<AddCardForm>
        id="add-card"
        className="grid-cols-2  gap-4"
        submitText={isPending ? 'Adding...' : 'Add Card'}
        defaultValues={defaultValues}
        includeSubmitButton={false}
        fields={[
          {
            name: 'number',
            label: 'Card Number',
            componentType: 'input',
            inputProps: { inputClassName: PRIVATE_FIELD_CLASS, 'data-private': true },
            zodValidation: cardNumberMultiRuleSchema(),
            errorClassName: 'error-list-format',
            getDynamicProps: (_, __, form) => ({
              inputProps: {
                placeholder: 'Enter card number',
                disabled: isPending,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  const val = e.currentTarget.value
                  form?.setFieldValue(
                    'number',
                    `${val
                      .replace(/[^\d]/g, '')
                      .replace(/(.{4})/g, '$1 ')
                      .trim()}`,
                  )
                  form?.validateField('number', 'change')
                },
              },
            }),
          },
          {
            name: 'year',
            label: 'Expiry',
            componentType: 'input',
            className: 'col-span-1',
            inputProps: { inputClassName: PRIVATE_FIELD_CLASS, 'data-private': true },
            zodValidation: cardExpiryMultiRuleSchema(),
            errorClassName: 'error-list-format',
            getDynamicProps: (_, __, form) => ({
              inputProps: {
                placeholder: 'MM/YY',
                maxLength: 5,
                disabled: isPending,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  const val = e.currentTarget.value.replace(/[^0-9/]/g, '')
                  form?.setFieldValue('year', `${val}`)
                  form?.validateField('year', 'change')
                },
                onKeyUp: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  const val = e.currentTarget.value
                  if (val.length === 2 && e.key !== 'Backspace') {
                    // Replace any character that is not a digit or forward slash with empty string
                    // This ensures only numbers and / are allowed in expiry date
                    form?.setFieldValue('year', `${val}/`)
                  }
                },
              },
            }),
          },
          {
            name: 'cvc',
            label: 'CVC/CVV',
            className: 'col-span-1',
            componentType: 'inputPassword',
            inputProps: { inputClassName: PRIVATE_FIELD_CLASS, 'data-private': true },
            zodValidation: cardCvcMultiRuleSchema(),
            errorClassName: 'error-list-format',
            getDynamicProps: (_, __, form) => ({
              inputProps: {
                maxLength: 4,
                placeholder: 'CVV',
                disabled: isPending,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  const val = e.currentTarget.value.replace(/[^\d]/g, '')
                  form?.setFieldValue('cvc', val)
                  form?.validateField('cvc', 'change')
                },
              },
            }),
          },
          {
            name: 'name',
            label: 'Card Holder Name',
            componentType: 'input',
            zodValidation: cardHolderNameMultiRuleSchema(),
            errorClassName: 'error-list-format',
            inputProps: {
              placeholder: 'Enter card holder name',
              disabled: isPending,
              inputClassName: PRIVATE_FIELD_CLASS,
              'data-private': true,
            },
          },
          {
            name: 'card_address',
            label: 'Copy from billing address',
            componentType: 'checkbox',
            zodValidation: z.boolean(),
            afterOnChange: (state, _, form) => {
              if (state.card_address && billingInformation) {
                form?.setFieldValue('addressline1', billingInformation.address_line_1 || '')
                form?.setFieldValue('city', billingInformation.city || '')
                form?.setFieldValue('state', billingInformation.state || '')
                form?.setFieldValue('zip', billingInformation.zipcode || '')
                form?.setFieldValue('country', billingInformation.country_id || '')
                setSelectedCountry(billingInformation.country_id || '')
                setSelectedState(billingInformation.state || '')
                setZipCode(billingInformation.zipcode || '')
              }
            },
          },
          {
            name: 'addressline1',
            label: 'Address',
            componentType: 'input',
            zodValidation: z.string().min(1, 'Address is required'),
            onChangeListenTo: ['card_address'],
            afterOnChange: (_state, _, form) => {
              if (form?.state.values.card_address) {
                form?.setFieldValue('card_address', false)
              }
            },
            getDynamicProps: () => ({
              inputProps: {
                placeholder: 'Enter address',
                disabled: isPending,
              },
            }),
          },
          {
            name: 'city',
            label: 'City',
            className: 'col-span-1',
            componentType: 'input',
            zodValidation: z.string().min(1, 'City is required'),
            onChangeListenTo: ['card_address'],
            afterOnChange: (_state, _, form) => {
              if (form?.state.values.card_address) {
                form?.setFieldValue('card_address', false)
              }
            },
            getDynamicProps: () => ({
              inputProps: {
                placeholder: 'Enter city',
                disabled: isPending,
              },
            }),
          },
          {
            name: 'state',
            label: 'State',
            className: 'col-span-1',
            componentType: getStateOptions(selectedCountry).length > 0 ? 'select' : 'input',
            onChangeListenTo: ['country', 'card_address'],
            zodValidation: z.string().min(1, 'State cannot be blank!'),
            afterOnChange: (state, _, form) => {
              handleStateChange(state)
              if (form?.state.values.card_address) {
                form?.setFieldValue('card_address', false)
              }
            },
            getDynamicProps: (_, __, form) => {
              const currentCountry = form?.state.values.country || selectedCountry
              return {
                inputProps:
                  getStateOptions(currentCountry).length > 0
                    ? {
                        options: getStateOptions(currentCountry),
                        placeholder: 'Select state',
                        disabled: isPending,
                      }
                    : {
                        placeholder: 'Enter state',
                        disabled: isPending,
                      },
              }
            },
          },
          {
            name: 'zip',
            componentType: 'input',
            className: 'col-span-1',
            onChangeListenTo: ['card_address', 'country', 'state'],
            afterOnChange: (state, _, form) => {
              handleZipCodeChange(state)
              if (form?.state.values.card_address) {
                form?.setFieldValue('card_address', false)
              }
            },
            getDynamicProps: (_, __, form) => {
              const currentCountry = form?.state.values.country || selectedCountry
              const isIndia = currentCountry === 'IN'
              const isSixDigits = zipCode.length === 6
              const apiError =
                zipCodeValidation?.status === false ? zipCodeValidation.errorMsg : undefined
              return {
                zodVal: zipCodeValidationSchema(currentCountry),
                inputProps: {
                  label: getPostalCodeLabel(currentCountry),
                  placeholder: `Enter ${getPostalCodeLabel(currentCountry)}`,
                  disabled: isPending,
                  endIcon: getZipCodeEndIcon(
                    getZipCodeStatus(
                      zipCode,
                      currentCountry,
                      zipCodeValidation,
                      isZipCodeValidating,
                    ),
                  ),
                  endIconClassName: 'pr-2',
                  error: isIndia && isSixDigits ? apiError : undefined,
                },
              }
            },
          },
          {
            name: 'country',
            label: 'Country',
            componentType: 'select',
            className: 'col-span-1',
            onChangeListenTo: ['card_address'],
            afterOnChange: (state, _, form) => {
              handleCountryChange(state)
              form?.setFieldValue('state', '')
              form?.setFieldValue('zip', '')
              setSelectedState('')
              setZipCode('')
              if (form?.state.values.card_address) {
                form?.setFieldValue('card_address', false)
              }
            },
            getDynamicProps: () => ({
              inputProps: {
                options: countryOptions(),
                placeholder: 'Select country',
                disabled: isIndiaCurrency || isPending,
              },
            }),
            zodValidation: z.string().min(1, 'Country is required'),
          },
          {
            name: 'isdefault',
            label: 'Make this the default card for payments',
            componentType: 'checkbox',
            zodValidation: z.boolean(),
            className: 'mb-2',
            inputProps: {
              disabled: isPending,
            },
            visible: !isFirstCard,
          },
        ]}
        onSubmit={(e) => {
          // Validate pincode for India if card_address is not checked (using custom address)
          // Use e.country from form state instead of selectedCountry to avoid stale state issues
          if (!e.card_address && e.country === 'IN' && e.zip.length === 6) {
            const zipCodeStatus = getZipCodeStatus(
              e.zip,
              e.country,
              zipCodeValidation,
              isZipCodeValidating,
            )

            if (zipCodeStatus === 'loading') {
              errorToast({
                title: 'Validation In Progress',
                description: 'Please wait for pincode validation to complete',
              })
              return
            }

            if (zipCodeStatus === 'invalid') {
              errorToast({
                title: 'Pincode Validation Error',
                description: 'Please enter a valid pincode for the selected state',
              })
              return
            }
          }

          const [month, year] = e.year.split('/')
          const { card_address: _, ...rest } = e
          addCard(
            { ...rest, month, year },
            {
              onSuccess: (data) => {
                queryClient.invalidateQueries({ queryKey: billingQueryKeys().cards(accountId) })
                if (data.redirect_url) {
                  // 3DS verification still required — card not actually added yet.
                  // Keep the modal open so the manual-verify fallback can show if
                  // the redirect is blocked. CARD_ADDED fires on the post-3DS
                  // callback path instead.
                  setVerifyUrl(data.redirect_url)
                  startPspRedirect(data.redirect_url)
                  return
                }
                onClose()
                track(AnalyticsEvents.CARD_ADDED, {
                  method_type: PaymentMethod.CARD,
                  country: data.card_country,
                })
                successToast({
                  title: 'Success',
                  description: 'Card added successfully',
                })
              },
              onError: (error) => {
                trackPaymentFailed('add_card', error, { country: e.country })
                const errorText =
                  `${getErrorText(error as AxiosError<ApiResponse<Response>>)}`.trim()
                errorToast({
                  title: 'Failed',
                  description: errorText ? `${errorText}` : 'Failed to add card',
                })
              },
            },
          )
        }}
      />
      {isFirstCard && (
        <div className="text-xs-400 text-secondary col-span-2 leading-relaxed">
          Note this card will be used as the default mode for future payments. By providing your
          card information, you authorise Plivo to charge your card for future payments in
          accordance with our{' '}
          <Link className="text-xs-600" to="https://www.plivo.com/legal/tos/" target="_blank">
            terms
          </Link>{' '}
          and{' '}
          <Link className="text-xs-600" to="https://www.plivo.com/legal/privacy/" target="_blank">
            privacy policy
          </Link>
        </div>
      )}
    </div>
  )
}
